import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FitnessCenter as TrainerIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthStore } from '../../store';
import { getAuthHeaders } from '../../utils/auth';

const TrainerPermissionManagement = () => {
  const { user } = useAuthStore();
  
  const [trainers, setTrainers] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedLessonTypes, setSelectedLessonTypes] = useState([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0); // Reset to first page on search
      fetchData();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (!user) {
        setError('Not authenticated. Please log in.');
        setLoading(false);
        return;
      }

      const idToken = await user.getIdToken();
      const headers = { Authorization: `Bearer ${idToken}` };

      // Build query parameters for pagination, search, and sorting
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        sort_order: sortOrder,
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (sortBy) {
        params.append('sort_by', sortBy);
      }

      // Fetch trainer permissions with pagination and lesson types
      const [permissionsRes, lessonTypesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainer-permissions?${params.toString()}`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type`, { headers }),
      ]);

      // Handle paginated response
      setTrainers(permissionsRes.data.items || []);
      setTotalCount(permissionsRes.data.total || 0);
      
      // Handle both response formats: direct array or { objects: [...] }
      const lessonTypesData = lessonTypesRes.data.objects || lessonTypesRes.data || [];
      setLessonTypes(lessonTypesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load trainer permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(column);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAssignDialog = async (trainer) => {
    try {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const idToken = await user.getIdToken();
      const headers = { Authorization: `Bearer ${idToken}` };

      // Fetch current lesson types for this trainer
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainers/${trainer.user_id}/lesson-types`,
        { headers }
      );

      setSelectedTrainer(trainer);
      setSelectedLessonTypes(response.data.lesson_type_ids || []);
      setOpenDialog(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch trainer lesson types');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTrainer(null);
    setSelectedLessonTypes([]);
  };

  const handleToggleLessonType = (lessonTypeId) => {
    setSelectedLessonTypes(prev => 
      prev.includes(lessonTypeId)
        ? prev.filter(id => id !== lessonTypeId)
        : [...prev, lessonTypeId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedTrainer) return;

    try {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const idToken = await user.getIdToken();
      const headers = { Authorization: `Bearer ${idToken}` };

      // Get current lesson type IDs
      const currentResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainers/${selectedTrainer.user_id}/lesson-types`,
        { headers }
      );
      const currentLessonTypeIds = currentResponse.data.lesson_type_ids || [];

      // Find lesson types to add
      const toAdd = selectedLessonTypes.filter(id => !currentLessonTypeIds.includes(id));
      
      // Find lesson types to remove
      const toRemove = currentLessonTypeIds.filter(id => !selectedLessonTypes.includes(id));

      // Add new permissions
      if (toAdd.length > 0) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainers/${selectedTrainer.user_id}/lesson-types`,
          { lesson_type_ids: toAdd },
          { headers }
        );
      }

      // Remove permissions
      for (const lessonTypeId of toRemove) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainers/${selectedTrainer.user_id}/lesson-types/${lessonTypeId}`,
          { headers }
        );
      }

      setSuccess(`Permissions updated for ${selectedTrainer.user_name}`);
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleRemovePermission = async (trainerId, lessonTypeId, trainerName, lessonTypeName) => {
    if (!window.confirm(`Remove "${lessonTypeName}" permission from ${trainerName}?`)) return;

    try {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const idToken = await user.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/trainers/${trainerId}/lesson-types/${lessonTypeId}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      setSuccess(`Permission removed from ${trainerName}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove permission');
    }
  };

  const totalPermissions = trainers.reduce((sum, trainer) => sum + trainer.total_permissions, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Zarządzanie Uprawnieniami Trenerów
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Przypisz typy zajęć, które trenerzy mogą prowadzić
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TrainerIcon color="primary" />
                <Typography color="textSecondary" gutterBottom>
                  Trenerzy
                </Typography>
              </Box>
              <Typography variant="h4">{trainers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CategoryIcon color="secondary" />
                <Typography color="textSecondary" gutterBottom>
                  Typy zajęć
                </Typography>
              </Box>
              <Typography variant="h4">{lessonTypes.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Łączna liczba uprawnień
              </Typography>
              <Typography variant="h4">{totalPermissions}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Szukaj trenera..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Trainers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Trener
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>Przypisane typy zajęć</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === 'permissions'}
                  direction={sortBy === 'permissions' ? sortOrder : 'asc'}
                  onClick={() => handleSort('permissions')}
                >
                  Liczba uprawnień
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trainers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    {searchTerm ? 'Nie znaleziono trenerów' : 'Brak trenerów w systemie'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              trainers.map((trainer) => (
                <TableRow key={trainer.user_id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TrainerIcon fontSize="small" color="primary" />
                      <Typography variant="body2" fontWeight={500}>
                        {trainer.user_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {trainer.user_email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {trainer.lesson_types.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          Brak uprawnień
                        </Typography>
                      ) : (
                        trainer.lesson_types.map((lessonType) => (
                          <Chip
                            key={lessonType.id}
                            label={lessonType.friendly_name || lessonType.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => 
                              handleRemovePermission(
                                trainer.user_id, 
                                lessonType.id, 
                                trainer.user_name,
                                lessonType.friendly_name || lessonType.name
                              )
                            }
                            deleteIcon={<DeleteIcon />}
                          />
                        ))
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={trainer.total_permissions} 
                      size="small" 
                      color={trainer.total_permissions > 0 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Zarządzaj uprawnieniami">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenAssignDialog(trainer)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
        />
      </TableContainer>

      {/* Assign Permissions Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TrainerIcon color="primary" />
            <Box>
              <Typography variant="h6">
                Zarządzaj uprawnieniami: {selectedTrainer?.user_name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Wybierz typy zajęć, które trener może prowadzić
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <FormGroup>
            {lessonTypes.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                Brak dostępnych typów zajęć
              </Typography>
            ) : (
              lessonTypes.map((lessonType) => (
                <FormControlLabel
                  key={lessonType.id}
                  control={
                    <Checkbox
                      checked={selectedLessonTypes.includes(lessonType.id)}
                      onChange={() => handleToggleLessonType(lessonType.id)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        {lessonType.friendly_name || lessonType.name}
                      </Typography>
                      {lessonType.friendly_name && (
                        <Typography variant="caption" color="textSecondary">
                          {lessonType.name}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>
            Anuluj
          </Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainerPermissionManagement;
